import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LogIn, LogOut, Clock, Users, AlertCircle, CheckCircle2 } from "lucide-react";

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
};

export default function PunchTerminal() {
  const { unitId } = useParams();
  const [searchParams] = useSearchParams();
  const [unit, setUnit] = useState(null);
  const [punchId, setPunchId] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | confirming | success | error
  const [employee, setEmployee] = useState(null);
  const [lastPunch, setLastPunch] = useState(null);
  const [board, setBoard] = useState(null);
  const [clock, setClock] = useState(new Date());
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const refreshBoard = async () => {
    try {
      const { data } = await api.get(`/punch-terminal/today/${unitId}`);
      setBoard(data);
    } catch (e) { console.error("[PunchTerminal] refreshBoard failed", e); }
  };

  useEffect(() => {
    api.get(`/units`).then(({ data }) => {
      setUnit((data || []).find((u) => u.id === unitId));
    });
    refreshBoard();
    const t = setInterval(refreshBoard, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [unitId]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [phase]);

  const lookup = async () => {
    if (!punchId.trim()) return;
    setPhase("confirming");
    try {
      const { data } = await api.post("/punch-terminal/lookup", { punch_id: punchId.trim() });
      setEmployee(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Lookup failed");
      setPhase("idle");
      setPunchId("");
    }
  };

  const confirmPunch = async () => {
    try {
      const { data } = await api.post("/punch-terminal/punch", {
        punch_id: punchId.trim(),
        unit_id: unitId,
        source: "terminal",
      });
      setLastPunch(data);
      setPhase("success");
      // Auto-reset after 4 seconds
      setTimeout(() => {
        setPhase("idle");
        setPunchId("");
        setEmployee(null);
        setLastPunch(null);
        refreshBoard();
        inputRef.current?.focus();
      }, 4000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Punch failed");
      setPhase("idle");
    }
  };

  const cancel = () => {
    setPhase("idle");
    setPunchId("");
    setEmployee(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 text-white p-6" data-testid="punch-terminal">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-60">BRAVURA PACKERS · PUNCH TERMINAL</div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: "Chivo" }}>
              {unit?.name || "Loading…"}
            </h1>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold font-mono" data-testid="clock">
              {clock.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })}
            </div>
            <div className="text-xs opacity-75">
              {clock.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* LEFT — Punch Input */}
          <div className="lg:col-span-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 shadow-2xl">
            {phase === "idle" && (
              <div className="space-y-4" data-testid="idle-state">
                <h2 className="text-xl font-bold">Enter your Punch ID</h2>
                <p className="text-xs opacity-75">Type your Punch ID and press Enter or Punch button.</p>
                <Input
                  ref={inputRef}
                  value={punchId}
                  onChange={(e) => setPunchId(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && lookup()}
                  className="bg-white text-slate-900 text-2xl font-bold h-16 text-center tracking-widest"
                  placeholder="BPL-001"
                  data-testid="punch-id-input"
                  autoFocus
                />
                <Button
                  onClick={lookup}
                  disabled={!punchId.trim()}
                  className="w-full h-14 brand-bg brand-bg-hover text-white text-lg font-bold"
                  data-testid="lookup-btn"
                >
                  <CheckCircle2 className="h-5 w-5 mr-2" /> Continue
                </Button>
              </div>
            )}

            {phase === "confirming" && employee && (
              <div className="space-y-3 text-center" data-testid="confirming-state">
                {employee.photo_url ? (
                  <img src={employee.photo_url} alt="" className="w-32 h-32 rounded-full mx-auto border-4 border-red-400 object-cover" />
                ) : (
                  <div className="w-32 h-32 rounded-full mx-auto bg-white/20 flex items-center justify-center text-5xl font-bold">
                    {(employee.name || "?")[0]}
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-bold" data-testid="emp-name">{employee.name}</h2>
                  <p className="text-sm opacity-75 font-mono">{employee.punch_id}</p>
                  {employee.designation && <p className="text-xs opacity-60">{employee.designation}</p>}
                </div>
                {employee.today && employee.today.punch_count > 0 && (
                  <div className="bg-white/10 rounded p-2 text-xs">
                    Already punched <b>{employee.today.punch_count}</b> time(s) today
                    {employee.today.first_in && <> · First IN: <b>{fmtTime(employee.today.first_in)}</b></>}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button onClick={cancel} variant="outline" className="flex-1 bg-white/5 border-white/30 text-white hover:bg-white/10" data-testid="cancel-btn">
                    Not Me
                  </Button>
                  <Button
                    onClick={confirmPunch}
                    className="flex-1 h-14 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-lg"
                    data-testid="confirm-btn"
                  >
                    {employee.today?.punch_count === 0 ? <LogIn className="h-5 w-5 mr-1" /> : <LogOut className="h-5 w-5 mr-1" />}
                    Punch {employee.today?.punch_count === 0 ? "IN" : (employee.today?.punch_count % 2 === 1 ? "OUT" : "IN")}
                  </Button>
                </div>
              </div>
            )}

            {phase === "success" && lastPunch && (
              <div className="space-y-3 text-center" data-testid="success-state">
                <div className="w-20 h-20 rounded-full bg-emerald-500 mx-auto flex items-center justify-center">
                  <CheckCircle2 className="h-12 w-12 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-emerald-300">{lastPunch.punch.punch_type === "IN" ? "Welcome!" : "Bye, see you soon!"}</h2>
                <p className="text-3xl font-mono font-bold">{lastPunch.punch.punch_time_hhmm}</p>
                <p className="text-sm">{lastPunch.employee.name} ({lastPunch.employee.punch_id})</p>
                <p className="text-xs opacity-75">Punch #{lastPunch.punch.punch_sequence} · {lastPunch.summary.worked_hhmm} worked today</p>
              </div>
            )}
          </div>

          {/* CENTER — Live Board */}
          <div className="lg:col-span-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Users className="h-5 w-5" /> Today's Activity
              </h2>
              {board && (
                <div className="text-xs opacity-75">
                  <span className="font-bold text-emerald-300">{board.currently_in}</span> inside ·{" "}
                  <span className="font-bold">{board.unique_employees}</span> punched ·{" "}
                  <span className="font-bold">{board.total_punches_today}</span> total
                </div>
              )}
            </div>
            {!board || board.rows.length === 0 ? (
              <div className="text-center opacity-50 py-12">
                <Clock className="h-12 w-12 mx-auto mb-2" />
                <p>No punches yet today. Be the first!</p>
              </div>
            ) : (
              <div className="overflow-y-auto max-h-[500px]" data-testid="board-list">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest opacity-60">
                    <tr className="text-left">
                      <th className="py-1 font-semibold">ID</th>
                      <th className="py-1 font-semibold">Name</th>
                      <th className="py-1 font-semibold">First IN</th>
                      <th className="py-1 font-semibold">Last</th>
                      <th className="py-1 font-semibold">Worked</th>
                      <th className="py-1 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {board.rows.map((r) => (
                      <tr key={r.employee_id} className="border-t border-white/10" data-testid={`board-row-${r.punch_id}`}>
                        <td className="py-1.5 font-mono text-xs">{r.punch_id}</td>
                        <td className="py-1.5 font-semibold">{r.employee_name}</td>
                        <td className="py-1.5 font-mono text-xs">{r.first_in_hhmm}</td>
                        <td className="py-1.5 font-mono text-xs">{r.last_punch_hhmm}</td>
                        <td className="py-1.5 font-mono text-xs">{r.worked_hhmm}</td>
                        <td className="py-1.5">
                          {r.status === "in" ? (
                            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/30 text-emerald-200">
                              ● Inside
                            </span>
                          ) : (
                            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-500/30 text-slate-200">
                              Left
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="text-center text-[10px] opacity-50 mt-6">
          Bravura Packers HR · Punch Terminal v1.0 · Auto-IN/OUT detection from first/last punch
        </div>
      </div>
    </div>
  );
}
